import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { SlidersHorizontal, X } from "lucide-react";

const swatch = (c) => String(c || "").trim().toLowerCase().replace(/\s+/g, "");
const PAGE_SIZE = 12;

export default function Catalog({ category: categoryProp, title }) {
  const { sub, occasion, key: routeKey } = useParams();
  const [params] = useSearchParams();
  const category = categoryProp || routeKey;
  const fabricType = params.get("type");
  const occ = occasion || params.get("occasion");
  const subQuery = sub || params.get("sub");

  const [all, setAll] = useState([]);
  const [subCats, setSubCats] = useState([]);
  const [catMeta, setCatMeta] = useState(null);
  const [occMeta, setOccMeta] = useState(null);

  const [sort, setSort] = useState("recent");
  const [colors, setColors] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [subs, setSubs] = useState(subQuery ? [subQuery] : []);
  const [price, setPrice] = useState({ min: "", max: "" });
  const [page, setPage] = useState(1);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!category) return;
    api.get("/product-categories", { params: { parent_key: category } }).then(({ data }) => setSubCats(data || [])).catch(() => {});
    if (!title) api.get("/product-categories", { params: { top_level: true } }).then(({ data }) => setCatMeta((data || []).find((c) => c.key === category) || null)).catch(() => {});
  }, [category]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!occ) { setOccMeta(null); return; }
    api.get("/seasonal-tiles").then(({ data }) => setOccMeta((data || []).find((t) => t.slug === occ) || null)).catch(() => {});
  }, [occ]);

  useEffect(() => {
    const q = new URLSearchParams();
    if (category) q.set("category", category);
    if (fabricType) q.set("fabric_type", fabricType);
    if (occ) q.set("occasion", occ);
    q.set("limit", 500);
    api.get(`/products?${q.toString()}`).then(({ data }) => setAll(data.items || [])).catch(() => setAll([]));
  }, [category, fabricType, occ]);

  useEffect(() => { setPage(1); }, [colors, sizes, subs, price, sort]);

  const facetColors = useMemo(() => [...new Set(all.flatMap((p) => p.colors || []))].filter(Boolean).sort(), [all]);
  const facetSizes = useMemo(() => [...new Set(all.flatMap((p) => p.sizes || []))].filter(Boolean), [all]);
  const priceBounds = useMemo(() => { const ps = all.map((p) => p.price || 0); return ps.length ? [Math.min(...ps), Math.max(...ps)] : [0, 0]; }, [all]);

  const toggle = (setter, arr, v) => setter(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const filtered = useMemo(() => {
    let items = all.filter((p) => {
      if (subs.length && !subs.includes(p.sub_category)) return false;
      if (colors.length && !(p.colors || []).some((c) => colors.includes(c))) return false;
      if (sizes.length && !(p.sizes || []).some((s) => sizes.includes(s))) return false;
      if (price.min !== "" && (p.price || 0) < Number(price.min)) return false;
      if (price.max !== "" && (p.price || 0) > Number(price.max)) return false;
      return true;
    });
    if (sort === "price_asc") items = [...items].sort((a, b) => a.price - b.price);
    if (sort === "price_desc") items = [...items].sort((a, b) => b.price - a.price);
    return items;
  }, [all, subs, colors, sizes, price, sort]);

  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const shown = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const heading = title || catMeta?.label || (subs[0] ? subs[0][0].toUpperCase() + subs[0].slice(1) : occ ? (occMeta?.name || occ) : "Catalog");
  const activeCount = colors.length + sizes.length + subs.length + (price.min !== "" || price.max !== "" ? 1 : 0);
  const clearAll = () => { setColors([]); setSizes([]); setSubs([]); setPrice({ min: "", max: "" }); setSort("recent"); };

  const Sidebar = () => (
    <div className="bg-white border border-black/10 rounded-xl p-5" data-testid="catalog-filters">
      <div className="flex items-center justify-between mb-4">
        <div className="font-display text-xl">Filters</div>
        {activeCount > 0 && <button onClick={clearAll} data-testid="filter-clear" className="text-xs text-squire-jeans uppercase tracking-[0.15em]">Clear all</button>}
      </div>

      <FilterGroup label="Sort Results">
        <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="filter-sort" className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm outline-none focus:border-squire-jeans">
          <option value="recent">Recently Added</option>
          <option value="price_asc">Price: Low to High</option>
          <option value="price_desc">Price: High to Low</option>
        </select>
      </FilterGroup>

      <FilterGroup label="Price Range">
        <div className="flex items-center gap-2">
          <input type="number" value={price.min} onChange={(e) => setPrice({ ...price, min: e.target.value })} placeholder={priceBounds[0] ? `${priceBounds[0]}` : "min"} data-testid="filter-price-min" className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm outline-none focus:border-squire-jeans" />
          <span className="text-[#9CA3AF]">-</span>
          <input type="number" value={price.max} onChange={(e) => setPrice({ ...price, max: e.target.value })} placeholder={priceBounds[1] ? `${priceBounds[1]}` : "max"} data-testid="filter-price-max" className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm outline-none focus:border-squire-jeans" />
        </div>
      </FilterGroup>

      {subCats.length > 0 && (
        <FilterGroup label="Category">
          <div className="space-y-1.5">
            {subCats.map((c) => (
              <label key={c.key} className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={subs.includes(c.key)} onChange={() => toggle(setSubs, subs, c.key)} data-testid={`filter-sub-${c.key}`} className="accent-squire-jeans" /> {c.label}
              </label>
            ))}
          </div>
        </FilterGroup>
      )}

      {facetColors.length > 0 && (
        <FilterGroup label="Colour">
          <div className="space-y-1.5">
            {facetColors.map((c) => (
              <label key={c} className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={colors.includes(c)} onChange={() => toggle(setColors, colors, c)} data-testid={`filter-color-${c}`} className="accent-squire-jeans" />
                <span className="w-3.5 h-3.5 rounded-full border border-black/20" style={{ background: swatch(c) }} /> {c}
              </label>
            ))}
          </div>
        </FilterGroup>
      )}

      {facetSizes.length > 0 && (
        <FilterGroup label="Size" last>
          <div className="flex flex-wrap gap-2">
            {facetSizes.map((s) => (
              <button key={s} onClick={() => toggle(setSizes, sizes, s)} data-testid={`filter-size-${s}`}
                className={`min-w-[38px] px-2 py-1.5 text-sm border rounded ${sizes.includes(s) ? "border-black bg-black text-white" : "border-black/20 hover:border-black"}`}>{s}</button>
            ))}
          </div>
        </FilterGroup>
      )}
    </div>
  );

  return (
    <main className="pt-8 sq-container" data-testid={`catalog-${category || subQuery || "all"}`}>
      <div className="border-b border-black/10 pb-6 mb-8">
        <div className="sq-eyebrow">Edit</div>
        <h1 className="font-display text-4xl md:text-5xl mt-2">{heading}</h1>
        <p className="text-sm text-[#4B5563] mt-2">{filtered.length} pieces</p>
      </div>

      <div className="lg:hidden mb-4">
        <button onClick={() => setMobileOpen(true)} className="inline-flex items-center gap-2 border border-black/20 rounded-lg px-4 py-2 text-sm"><SlidersHorizontal size={16} /> Filters{activeCount ? ` (${activeCount})` : ""}</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8">
        <aside className="hidden lg:block"><Sidebar /></aside>

        <div>
          {shown.length === 0 ? (
            <div className="border border-black/10 rounded-xl p-16 text-center text-[#4B5563]">No pieces match these filters.</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-5 md:gap-6">
              {shown.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}

          {pages > 1 && (
            <div className="mt-10 flex justify-center gap-2" data-testid="catalog-pagination">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-2 border border-black/20 rounded disabled:opacity-40">Prev</button>
              {Array.from({ length: pages }).slice(0, 7).map((_, i) => (
                <button key={i} onClick={() => setPage(i + 1)} data-testid={`page-${i + 1}`} className={`w-10 py-2 border rounded ${page === i + 1 ? "border-black bg-black text-white" : "border-black/20"}`}>{i + 1}</button>
              ))}
              <button onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page === pages} className="px-3 py-2 border border-black/20 rounded disabled:opacity-40">Next</button>
            </div>
          )}

          {category === "readymade" && (
            <section className="mt-16 bg-black text-white p-10 flex flex-col md:flex-row items-center gap-8" data-testid="bespoke-cta-banner">
              <div className="flex-1">
                <div className="sq-eyebrow text-white/60">Didn't find a perfect fit?</div>
                <h3 className="font-display text-3xl md:text-4xl mt-2">Then tailor it to you.</h3>
              </div>
              <Link to="/bespoke" data-testid="catalog-bespoke-cta" className="sq-btn-ghost-white">Try Bespoke</Link>
            </section>
          )}
        </div>
      </div>

      {mobileOpen && (
        <div className="fixed inset-0 z-[80] lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-[85%] max-w-sm bg-[#FAFAF8] overflow-y-auto p-4">
            <div className="flex justify-end"><button onClick={() => setMobileOpen(false)}><X size={22} /></button></div>
            <Sidebar />
            <button onClick={() => setMobileOpen(false)} className="sq-btn-primary w-full mt-4">Show {filtered.length} results</button>
          </div>
        </div>
      )}
    </main>
  );
}

function FilterGroup({ label, children, last }) {
  return (
    <div className={`${last ? "" : "border-b border-black/10 pb-4 mb-4"}`}>
      <div className="sq-eyebrow text-[#4B5563] mb-2">{label}</div>
      {children}
    </div>
  );
}
